import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  APPLE_SUBSCRIPTIONS_URL,
  GOOGLE_PLAY_SUBSCRIPTIONS_URL,
  CANCELLATION_EMAIL,
  TERMS_URL,
} from "../../externalLinks";
import { withEntryQuery } from "../../entryParams";
import { CANCELLATION_CONFIRM_PATH } from "../../routes";
import "../public-page.css";

/**
 * Step one of the login-free cancellation: the "Verträge hier kündigen" button of
 * § 312k Abs. 2 BGB, permanently reachable and not behind a login. It leads
 * straight to the confirmation page - no token, no login prompt, no detour - and
 * collects nothing itself.
 */
function CancellationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="pub-page">
      <button
        type="button"
        className="pub-back-link"
        onClick={() => navigate(withEntryQuery("/"))}
      >
        {t("app.backHome")}
      </button>

      <h1 className="pub-title">{t("cancellation.start.title")}</h1>
      <p className="pub-lead">{t("cancellation.start.lead")}</p>

      <div className="pub-notice pub-notice-info">
        <p>
          {t("cancellation.start.storeNotice")}{" "}
          <a
            className="pub-link"
            href={APPLE_SUBSCRIPTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("cancellation.start.storeLink")}
          </a>
          {" · "}
          <a
            className="pub-link"
            href={GOOGLE_PLAY_SUBSCRIPTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("cancellation.start.storeLinkGoogle")}
          </a>
        </p>
      </div>

      <section
        className="pub-panel"
        aria-label={t("cancellation.start.stepsTitle")}
      >
        <h2 className="pub-panel-title">{t("cancellation.start.stepsTitle")}</h2>
        <ol className="pub-steps">
          <li>{t("cancellation.start.steps.details")}</li>
          <li>{t("cancellation.start.steps.review")}</li>
          <li>{t("cancellation.start.steps.confirm")}</li>
        </ol>

        {/*
          The button label is prescribed by § 312k Abs. 2 Satz 2 BGB: it must not
          be softened ("Abo verwalten") or paired with a competing call to action.
        */}
        <button
          type="button"
          className="pub-primary-btn pub-primary-btn-block"
          onClick={() => navigate(withEntryQuery(CANCELLATION_CONFIRM_PATH))}
        >
          {t("cancellation.start.button")}
        </button>
        <p className="pub-submit-hint">{t("cancellation.start.buttonHint")}</p>
      </section>

      <section
        className="pub-panel"
        aria-label={t("cancellation.start.effectTitle")}
      >
        <h2 className="pub-panel-title">
          {t("cancellation.start.effectTitle")}
        </h2>
        <ul className="pub-list">
          <li>{t("cancellation.start.effect.immediate")}</li>
          <li>{t("cancellation.start.effect.paidPeriod")}</li>
          <li>{t("cancellation.start.effect.trial")}</li>
          <li>{t("cancellation.start.effect.account")}</li>
          <li>{t("cancellation.start.effect.afterEnd")}</li>
          {/*
            AGB 19.1 promises this notice "im Kündigungsvorgang": the lock reaches
            the user's own lyrics, and it is the one consequence people do not
            expect. The exception belongs next to it - without it the warning reads
            as a threat to somebody whose content fits into the free tier anyway.
          */}
          <li>{t("cancellation.start.effect.lyrics")}</li>
          <li>{t("cancellation.start.effect.noLockException")}</li>
        </ul>
        <p className="pub-text">
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="pub-link"
          >
            {t("cancellation.start.termsLink")}
          </a>
        </p>
      </section>

      <section
        className="pub-panel"
        aria-label={t("cancellation.start.otherWaysTitle")}
      >
        <h2 className="pub-panel-title">
          {t("cancellation.start.otherWaysTitle")}
        </h2>
        <ul className="pub-list">
          <li>{t("cancellation.start.otherWays.portal")}</li>
          <li>
            {t("cancellation.start.otherWays.mail")}{" "}
            <a href={`mailto:${CANCELLATION_EMAIL}`} className="pub-link">
              {CANCELLATION_EMAIL}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}

export default CancellationPage;
